/**
 * Workspace roster loader — the ONE answer to "who is in this workspace?" for
 * the add-people picker (group-threading P2c).
 *
 * Deliberately NOT re-exported from `./index`: this module touches firebase, and
 * the pure model modules behind that barrel must stay importable in the node
 * unit environment. Callers import it by path; the decisions it makes live in
 * `workspaceRosterModel` and are tested there.
 *
 * Two reads, reconciled:
 *   1. `rbacListMembers` — the server-resolved account. This is byte-for-byte
 *      the call Team & Roles renders, so the picker can no longer disagree with
 *      the page the admin just came from. It also reports back which account the
 *      server resolved, which is how we DETECT a split-brain instead of shrugging.
 *   2. `accounts/{accountId}/members` — the workspace the thread write path
 *      (`threadMembershipWrite`) admits participants against. Also the fallback
 *      when the callable is unavailable (offline, cold start, a member without
 *      `team.view`).
 *
 * Failure is never silent. `error` is set only when BOTH reads failed, and the
 * caller renders a retry instead of the "nobody here" empty state.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { getAccountMembers } from '../prest0nRuntime/accountMembership';
import {
  describeRosterFailure,
  mergeRosterSources,
  normalizePendingInvites,
  normalizeRosterEntries,
  type NormalizedRosterEntry,
  type PendingInviteEntry,
} from './workspaceRosterModel';

export interface WorkspaceRosterResult {
  entries: NormalizedRosterEntry[];
  /** Invited, not yet joined. Shown as context, never as addable. */
  pending: PendingInviteEntry[];
  /** The account the SERVER resolved for this caller, when it answered. */
  resolvedAccountId: string | null;
  /** Set only when every roster read failed — the caller shows a retry. */
  error: string | null;
  /** Which reads actually produced the roster (diagnostics / tests). */
  sources: Array<'server' | 'firestore'>;
}

const EMPTY: WorkspaceRosterResult = {
  entries: [],
  pending: [],
  resolvedAccountId: null,
  error: null,
  sources: [],
};

async function callListMembers(): Promise<{ members: unknown[]; resolvedAccountId: string | null }> {
  const fn = httpsCallable<Record<string, never>, { members?: unknown[]; resolvedAccountId?: string }>(
    getFunctions(getApp()),
    'rbacListMembers',
  );
  const res = await fn({});
  return {
    members: Array.isArray(res.data?.members) ? res.data.members : [],
    resolvedAccountId: (res.data?.resolvedAccountId ?? '').trim() || null,
  };
}

async function callListInvitations(): Promise<unknown[]> {
  const fn = httpsCallable<Record<string, never>, { invitations?: unknown[] }>(
    getFunctions(getApp()),
    'rbacListInvitations',
  );
  const res = await fn({});
  return Array.isArray(res.data?.invitations) ? res.data.invitations : [];
}

export interface LoadWorkspaceRosterOptions {
  /**
   * Instant-open (2026-08-09): when provided, the returned result resolves
   * after the two MEMBER reads alone and pending invitations are delivered
   * HERE once their (slower, display-context-only) callable settles — the
   * addable list must never wait on the "Invited" section. Rejections are
   * swallowed (pending invites are context, not the roster). Without it,
   * behavior is unchanged: one settle carrying everything.
   */
  onPending?: (pending: PendingInviteEntry[]) => void;
}

/**
 * Load the roster for `accountId`. Never throws — a total failure comes back as
 * `error` so the picker can offer a retry rather than claim the workspace is
 * empty.
 */
export async function loadWorkspaceRoster(
  accountId: string,
  opts: LoadWorkspaceRosterOptions = {},
): Promise<WorkspaceRosterResult> {
  const account = (accountId ?? '').trim();
  if (!account) return { ...EMPTY };

  const invitesPromise = callListInvitations();
  // Detached rejection guard: a fast-failing invitations callable must not
  // fire `unhandledrejection` while the (slower) member reads are still
  // settling below. The original promise is still consumed for delivery.
  void invitesPromise.catch(() => undefined);
  // Both member reads run together and are settled independently — one failing
  // must not cost us the other's result. Invitations deliberately sit OUTSIDE
  // this barrier when onPending is provided (see LoadWorkspaceRosterOptions):
  // pre-fix, the roster paint waited on max(members, firestore, invitations),
  // with each callable paying the full server-side account-resolution cost.
  const [serverRes, localRes] = await Promise.allSettled([
    callListMembers(),
    // `fromServer` so a member seeded moments ago on another client shows up
    // now; `strict` so a rules denial surfaces instead of masquerading as [].
    getAccountMembers(account, { fromServer: true, strict: true }),
  ]);

  const sources: Array<'server' | 'firestore'> = [];
  const groups: NormalizedRosterEntry[][] = [];
  let resolvedAccountId: string | null = null;

  if (serverRes.status === 'fulfilled') {
    sources.push('server');
    resolvedAccountId = serverRes.value.resolvedAccountId;
    groups.push(normalizeRosterEntries(serverRes.value.members, 'server'));
  }
  if (localRes.status === 'fulfilled') {
    sources.push('firestore');
    groups.push(normalizeRosterEntries(localRes.value, 'firestore'));
  }

  if (sources.length === 0) {
    // Prefer the server's reason — it is the more specific of the two.
    const reason =
      serverRes.status === 'rejected' ? serverRes.reason : (localRes as PromiseRejectedResult).reason;
    // Invitations may still be in flight — already detached above.
    return { ...EMPTY, error: describeRosterFailure(reason) };
  }

  // A split-brain here is exactly the defect this loader exists to expose: the
  // RBAC resolver and the session/threading resolver disagreeing about which
  // account this user is in. Merging keeps the human unblocked; the warning is
  // what lets us fix the data.
  if (resolvedAccountId && resolvedAccountId !== account) {
    console.warn(
      '[threadAddPeople] workspace mismatch — the server resolved a different account than this session ' +
        `(server=${resolvedAccountId} session=${account}). Rosters were merged; ` +
        'adds are still authorized against the session account by threadMembershipWrite.',
    );
  }

  const entries = mergeRosterSources(...groups);
  const memberEmails = entries.map((e) => e.email ?? '');

  if (opts.onPending) {
    // Deliver pending invitations off the critical path; the roster returns now.
    void invitesPromise
      .then((invites) => opts.onPending?.(normalizePendingInvites(invites, memberEmails)))
      .catch(() => undefined); // non-fatal: pending invites are context, not the roster
    return { entries, pending: [], resolvedAccountId, error: null, sources };
  }

  const invitesRes = await invitesPromise.then(
    (value) => ({ status: 'fulfilled' as const, value }),
    () => ({ status: 'rejected' as const }),
  );
  const pending =
    invitesRes.status === 'fulfilled'
      ? normalizePendingInvites(invitesRes.value, memberEmails)
      : []; // non-fatal: pending invites are context, not the roster

  return { entries, pending, resolvedAccountId, error: null, sources };
}
